import { createApiClient, apiClient } from "../../api/apiClient";
import { Dispatch } from "@reduxjs/toolkit";
import { setObservationQuestions } from "../../store/slices/observationSlice";
import store from "../../store";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../../store/utils/observationErrorHandler";

interface ObservationQuestion {
  id: number;
  questionText: string;
  observationType: string;
  roleObserved: string;
}

interface ApiQuestionResponse {
  ID: number;
  Question: string;
  Observation: string;
  OBSRVTN_TYPE_NAME: string;
  ROLE_NAME: string;
  [key: string]: any;
}

/**
 * Fetches observation questions from the API
 * @param role The role to filter questions by
 * @param observationType The observation type to filter questions by
 * @param dispatch Redux dispatch function
 */
const fetchObservationQuestions = async (
  role: string,
  observationType: string,
  dispatch: Dispatch
): Promise<ObservationQuestion[]> => {
  try {
    // Check if questions are already loaded for this role and type
    const state = store.getState();
    const existingQuestions = state.observation.observationQuestions;
    
    // If we have questions for the same role and observation type, return them
    if (existingQuestions && existingQuestions.length > 0) {
      const matchingQuestions = existingQuestions.filter(
        (q) => q.roleObserved === role && q.observationType === observationType
      );
      if (matchingQuestions.length > 0) {
        console.log("Using cached observation questions");
        return matchingQuestions;
      }
    }

    // Create API client
    const api = createApiClient();

    // Fetch questions from API
    console.log(`Fetching observation questions for role: ${role}, type: ${observationType}`);
    const response = await apiClient(
      api,
      `observations/questions?role=${encodeURIComponent(role)}&observation_type=${encodeURIComponent(observationType)}`,
      "GET"
    );

    // Transform API response to match our interface
    const transformedQuestions: ObservationQuestion[] = [];
    
    if (response && response.data) {
      Object.values(response.data).forEach((questionArray: any) => {
        if (Array.isArray(questionArray)) {
          questionArray.forEach((question: ApiQuestionResponse) => {
            transformedQuestions.push({
              id: question.ID,
              questionText: question.Observation || question.Question,
              observationType: question.OBSRVTN_TYPE_NAME || observationType,
              roleObserved: question.ROLE_NAME || role,
            });
          });
        }
      });
    }

    console.log(`Fetched ${transformedQuestions.length} observation questions`);

    // Update Redux store with questions
    dispatch(setObservationQuestions(transformedQuestions));

    return transformedQuestions;
  } catch (error) {
    console.error("Error fetching observation questions:", error);
    return [];
  }
};

/**
 * Fetches detailed information about a specific observation
 * @param observationId The ID of the observation to fetch
 * @param setIsDetailLoading Function to set the loading state
 * @param setDetailApiError Function to set any error that occurs
 * @param setDetailedObservationData Function to set the observation data
 * @param dispatch Redux dispatch function
 */
export const fetchObservationDetails = async (
  observationId: number,
  setIsDetailLoading: (isLoading: boolean) => void,
  setDetailApiError: (error: string | null) => void,
  setDetailedObservationData: (data: any | null) => void,
  dispatch: Dispatch
): Promise<void> => {
  setIsDetailLoading(true);
  setDetailApiError(null);
  setDetailedObservationData(null);

  try {
    // Create API client instance
    const api = createApiClient();

    // Use apiClient instead of fetch
    const data = await apiClient(api, `/observations/${observationId}`, "GET");
    console.log("Fetched observation details:", data);
    
    // Extract role and observation type from the data
    const role = data.Role_Observed || "";
    const observationType = data.New_Hire_Annual_or_Routine || "";

    // Fetch questions if we have role and observation type
    if (role && observationType) {
      await fetchObservationQuestions(role, observationType, dispatch);
    }

    setDetailedObservationData(data);
  } catch (err) {
    console.error("Error fetching observation details:", err);
    
    // Show error overlay modal
    handleObservationErrorWithFriendlyMessage(
      OBSERVATION_API_NAMES.FETCH_OBSERVATION_DETAILS,
      err,
      true, // Show modal overlay
      {
        observationId,
      }
    );
    
    setDetailApiError(
      err instanceof Error ? err.message : "Failed to fetch observation details"
    );
  } finally {
    setIsDetailLoading(false);
  }
};
